import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowUpRight } from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { StagePage } from '@/components/generals/stage-page'
import { StageHeading } from '@/components/generals/stage-heading'
import { IMG_BASE, PHOTOS, META } from '@/components/sections/amour/data'

import { ABOUT, STUDIO_INFO } from '@/constants/studio'

/* Eight frames lifted straight out of the archive the front page assembles.
 * Spaced across the set so the strip is not eight variations of one gel. */
const STRIP = [2, 7, 13, 19, 24, 30, 35, 38]

const About: FC = (): ReactNode => {
    return (
        <>
            <Seo
                title='About'
                description='Amour is a portrait studio in Lisbon. One gel, one figure, one sheet of seamless, and ten years of staying inside those rules.'
            />

            <StagePage active='/about'>
                <section
                    id='hero'
                    className='mx-auto max-w-[1200px] px-6 pt-20 pb-16 sm:px-10 sm:pt-28 lg:pt-36'
                >
                    <div className='animate-in fade-in slide-in-from-bottom-3 fill-mode-both duration-700 ease-out motion-reduce:animate-none'>
                        <StageHeading
                            level={1}
                            label={ABOUT.label}
                            small={ABOUT.headSmall}
                            large={ABOUT.headLarge}
                        />
                    </div>

                    <div className='mt-12 grid gap-8 border-t border-white/10 pt-8 md:grid-cols-[1.4fr_1fr] md:gap-16'>
                        <p className='max-w-[62ch] text-[15px] leading-[1.75] text-[#F4F1EA]/70 sm:text-base'>
                            {ABOUT.lede}
                        </p>

                        <dl className='grid grid-cols-2 gap-6 text-[12px] md:grid-cols-1 md:gap-5'>
                            <div>
                                <dt className='text-[10px] uppercase tracking-[0.3em] text-[#F4F1EA]/40'>
                                    Founded
                                </dt>
                                <dd className='mt-2 text-[#F4F1EA]/80'>
                                    2016, Lisbon
                                </dd>
                            </div>

                            <div>
                                <dt className='text-[10px] uppercase tracking-[0.3em] text-[#F4F1EA]/40'>
                                    Discipline
                                </dt>
                                <dd className='mt-2 text-[#F4F1EA]/80'>
                                    Portraiture, gelled
                                </dd>
                            </div>
                        </dl>
                    </div>
                </section>

                <section
                    id='story'
                    className='mx-auto max-w-[1200px] px-6 py-16 sm:px-10 sm:py-24'
                >
                    <div className='grid gap-10 md:grid-cols-[1fr_1.4fr] md:gap-16'>
                        <StageHeading
                            label={ABOUT.story.label}
                            small={ABOUT.story.headSmall}
                            large={ABOUT.story.headLarge}
                            className='md:sticky md:top-28 md:self-start'
                        />

                        <div className='space-y-6'>
                            {ABOUT.story.body.map((paragraph) => (
                                <p
                                    key={paragraph.slice(0, 24)}
                                    className='max-w-[68ch] text-[15px] leading-[1.8] text-[#F4F1EA]/70 sm:text-base'
                                >
                                    {paragraph}
                                </p>
                            ))}
                        </div>
                    </div>

                    <ul className='mt-14 grid grid-cols-4 gap-1.5 sm:gap-2 lg:grid-cols-8'>
                        {STRIP.map((index) => (
                            <li key={PHOTOS[index]} className='overflow-hidden rounded-[3px]'>
                                <img
                                    src={`${IMG_BASE}${PHOTOS[index]}`}
                                    alt={`Studio portrait of ${META[index].name}`}
                                    loading='lazy'
                                    decoding='async'
                                    className='aspect-[4/5] w-full object-cover opacity-80 transition duration-500 ease-out hover:scale-[1.03] hover:opacity-100'
                                />
                            </li>
                        ))}
                    </ul>
                </section>

                <section
                    id='numbers'
                    className='border-y border-white/10'
                    aria-label='The studio in numbers'
                >
                    {/* Hairlines drawn by the gap, not by per-cell borders — one
                        rule instead of four conditional ones, and it survives the
                        column count changing at the breakpoint. */}
                    <dl className='mx-auto grid max-w-[1200px] grid-cols-2 gap-px bg-white/10 lg:grid-cols-4'>
                        {ABOUT.stats.map((stat) => (
                            <div key={stat.label} className='bg-[#050506] px-6 py-12 sm:px-10'>
                                <dt className='sr-only'>
                                    {stat.label}
                                </dt>
                                <dd>
                                    <span className='block font-heading text-[clamp(44px,7vw,84px)] leading-none'>
                                        {stat.figure}
                                    </span>
                                    <span className='mt-4 block max-w-[18ch] text-[11px] uppercase tracking-[0.22em] text-[#F4F1EA]/45'>
                                        {stat.label}
                                    </span>
                                </dd>
                            </div>
                        ))}
                    </dl>
                </section>

                <section
                    id='process'
                    className='mx-auto max-w-[1200px] px-6 py-16 sm:px-10 sm:py-24'
                >
                    <StageHeading
                        label={ABOUT.process.label}
                        small={ABOUT.process.headSmall}
                        large={ABOUT.process.headLarge}
                    />

                    <ol className='mt-14 grid gap-px overflow-hidden rounded-md bg-white/10 sm:grid-cols-2'>
                        {ABOUT.process.steps.map((step) => (
                            <li key={step.n} className='bg-[#050506] p-7 sm:p-9'>
                                <span className='font-mono text-[11px] tracking-[0.2em] text-[#4EA3FF]'>
                                    {step.n}
                                </span>

                                <h3 className='mt-4 font-heading text-2xl leading-none sm:text-[28px]'>
                                    {step.title}
                                </h3>

                                <p className='mt-4 max-w-[46ch] text-[14px] leading-[1.75] text-[#F4F1EA]/65'>
                                    {step.body}
                                </p>
                            </li>
                        ))}
                    </ol>
                </section>

                <section
                    id='people'
                    className='mx-auto max-w-[1200px] px-6 py-16 sm:px-10 sm:py-24'
                >
                    <span className='inline-flex items-center gap-2 text-[10.5px] font-medium uppercase tracking-[0.3em] text-[#F4F1EA]/50'>
                        <i aria-hidden className='not-italic opacity-70'>✳</i>
                        {ABOUT.people.label}
                    </span>

                    <ul className='mt-8 grid gap-10 border-t border-white/10 pt-10 sm:grid-cols-2 sm:gap-16'>
                        {ABOUT.people.members.map((member) => (
                            <li key={member.name}>
                                <h3 className='font-heading text-[clamp(26px,3.4vw,40px)] leading-none'>
                                    {member.name}
                                </h3>

                                <p className='mt-3 text-[11px] uppercase tracking-[0.22em] text-[#4EA3FF]/85'>
                                    {member.role}
                                </p>

                                <p className='mt-5 max-w-[46ch] text-[14px] leading-[1.75] text-[#F4F1EA]/65'>
                                    {member.note}
                                </p>
                            </li>
                        ))}
                    </ul>
                </section>

                <section
                    id='credits'
                    className='mx-auto max-w-[1200px] px-6 pb-16 sm:px-10 sm:pb-24'
                >
                    <h2 className='text-[10.5px] font-medium uppercase tracking-[0.3em] text-[#F4F1EA]/45'>
                        {ABOUT.credits.label}
                    </h2>

                    <ul className='mt-6 flex flex-wrap gap-2'>
                        {ABOUT.credits.items.map((item) => (
                            <li
                                key={item}
                                className='rounded-full bg-white/[0.09] px-3.5 py-1.5 text-[11px] tracking-[0.04em] text-[#F4F1EA]/75'
                            >
                                {item}
                            </li>
                        ))}
                    </ul>
                </section>

                <section
                    id='cta'
                    className='border-t border-white/10'
                >
                    <div className='mx-auto flex max-w-[1200px] flex-col gap-10 px-6 py-20 sm:px-10 sm:py-28 lg:flex-row lg:items-end lg:justify-between'>
                        <StageHeading
                            small={ABOUT.cta.headSmall}
                            large={ABOUT.cta.headLarge}
                        />

                        <div className='lg:max-w-[38ch]'>
                            <p className='text-[14px] leading-[1.75] text-[#F4F1EA]/65'>
                                {ABOUT.cta.body}
                            </p>

                            <Link
                                href='/contact'
                                className='mt-7 inline-flex items-center gap-2.5 rounded-full border border-white/20 bg-white/[0.06] px-6 py-3 text-[12px] uppercase tracking-[0.2em] transition-colors duration-300 hover:border-white/40 hover:bg-white/[0.12] focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#4EA3FF]'
                            >
                                {ABOUT.cta.action}
                                <ArrowUpRight className='size-4' aria-hidden />
                            </Link>

                            <p className='mt-6 text-[11px] uppercase tracking-[0.22em] text-[#F4F1EA]/40'>
                                {STUDIO_INFO.address[0]} — {STUDIO_INFO.hours}
                            </p>
                        </div>
                    </div>
                </section>
            </StagePage>
        </>
    )
}

export default About
